/**
 * Utility functions for handling date parsing and formatting
 */

/**
 * Safely parse a date string that might come from Firestore or other sources
 * @param dateInput - The date string or timestamp to parse
 * @returns A valid Date object or null if parsing fails
 */
export function safeParseDate(dateInput: string | number | Date | null | undefined): Date | null {
  if (!dateInput) {
    return null;
  }

  try {
    // If it's already a Date object, return it
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // If it's a number (timestamp), convert to Date
    if (typeof dateInput === 'number') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }

    // If it's a string, try various parsing methods
    if (typeof dateInput === 'string') {
      // Clean up the date string - remove timezone identifiers like [GMT], [UTC], etc.
      let cleanedDateString = dateInput;

      // Remove timezone identifiers in brackets like [GMT], [UTC]
      cleanedDateString = cleanedDateString.replace(/\[[\w\/]+\]$/, '');

      // Try parsing the cleaned string first
      let date = new Date(cleanedDateString);
      if (!isNaN(date.getTime())) {
        return date;
      }

      // Try direct parsing of original string
      date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        return date;
      }

      // Try parsing ISO string format (common from APIs)
      if (dateInput.includes('T') || dateInput.includes('Z')) {
        date = new Date(cleanedDateString);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // Try parsing timestamp as string
      const timestamp = parseInt(dateInput, 10);
      if (!isNaN(timestamp)) {
        date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }

    return null;
  } catch (error) {
    console.warn('Failed to parse date:', dateInput, error);
    return null;
  }
}

/**
 * Get user's timezone information
 */
export function getUserTimezone(): {
  timeZone: string;
  offset: number;
  offsetString: string;
} {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const offsetSign = offset <= 0 ? '+' : '-';
  const offsetString = `UTC${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

  return {
    timeZone,
    offset,
    offsetString
  };
}

/**
 * Format a date safely with timezone awareness
 * @param dateInput - The date to format
 * @param fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @returns Formatted date string in user's timezone or fallback text
 */
export function formatDate(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown date'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const userTz = getUserTimezone();
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: userTz.timeZone
    });
  } catch (error) {
    console.warn('Failed to format date with timezone:', date, error);
    try {
      return date.toLocaleDateString();
    } catch (fallbackError) {
      console.warn('Failed to format date with fallback:', date, fallbackError);
      return fallbackText;
    }
  }
}

/**
 * Format a date with time safely with timezone awareness
 * @param dateInput - The date to format
 * @param fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @returns Formatted date and time string in user's timezone or fallback text
 */
export function formatDateTime(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown date'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const userTz = getUserTimezone();
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTz.timeZone,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.warn('Failed to format datetime with timezone:', date, error);
    try {
      return date.toLocaleString();
    } catch (fallbackError) {
      console.warn('Failed to format datetime with fallback:', date, fallbackError);
      return fallbackText;
    }
  }
}

/**
 * Get a relative time string (e.g., "2 days ago")
 * @param dateInput - The date to compare
 * @param fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @returns Relative time string or fallback text
 */
export function formatRelativeTime(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown date'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.warn('Failed to format relative time:', date, error);
    return fallbackText;
  }
}

/**
 * Format a date for detailed display with timezone info
 * @param dateInput - The date to format
 * @param fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @returns Detailed formatted date string with timezone
 */
export function formatDateDetailed(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown date'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const userTz = getUserTimezone();
    return date.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTz.timeZone,
      timeZoneName: 'long'
    });
  } catch (error) {
    console.warn('Failed to format detailed date with timezone:', date, error);
    return formatDateTime(dateInput, fallbackText);
  }
}

/**
 * Format just the time portion in user's timezone
 * @param dateInput - The date to format
 * @param fallbackText - Text to show if date is invalid (default: 'Unknown time')
 * @returns Formatted time string in user's timezone
 */
export function formatTime(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown time'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const userTz = getUserTimezone();
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTz.timeZone,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.warn('Failed to format time with timezone:', date, error);
    try {
      return date.toLocaleTimeString();
    } catch (fallbackError) {
      console.warn('Failed to format time with fallback:', date, fallbackError);
      return fallbackText;
    }
  }
}

/**
 * Get a user-friendly date and time display
 * Shows relative time for recent dates, absolute for older ones
 * @param dateInput - The date to format
 * @param fallbackText - Text to show if date is invalid
 * @returns Smart formatted date string
 */
export function formatDateSmart(
  dateInput: string | number | Date | null | undefined,
  fallbackText: string = 'Unknown date'
): string {
  const date = safeParseDate(dateInput);

  if (!date) {
    return fallbackText;
  }

  try {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Show relative time for recent dates (within 7 days)
    if (diffDays >= 0 && diffDays < 7) {
      return formatRelativeTime(dateInput, fallbackText);
    } else {
      // Show absolute date for older dates
      return formatDateTime(dateInput, fallbackText);
    }
  } catch (error) {
    console.warn('Failed to format smart date:', date, error);
    return formatDate(dateInput, fallbackText);
  }
}

/**
 * Convert a date-only input value (YYYY-MM-DD) to a UTC ISO string for backend persistence.
 * Uses noon UTC to ensure the date displays correctly in all timezones (UTC-12 through UTC+14).
 * @param dateValue - A date string in YYYY-MM-DD format (from an HTML date input)
 * @returns UTC ISO string with time set to 12:00:00.000Z
 */
export function dateInputToUTCString(dateValue: string): string {
  return dateValue + 'T12:00:00.000Z';
}

/**
 * The browser's LOCAL calendar date as `yyyy-MM-dd`, for dating auto-created ACQUISITION records.
 *
 * Price history dates are pinned to NOON UTC so the calendar date survives any viewer's timezone.
 * Manual price entry already sends a local date. Auto-created acquisition records had no such
 * input, so slide-svc stamped the capture instant and an evening capture landed on the FOLLOWING
 * UTC day, making the date the UI showed disagree with the date every report bucketed on.
 *
 * The server cannot derive this: it has no shop timezone, and noon UTC of the UTC date would be
 * the wrong DAY for an evening capture. So the client sends its local date and the server pins it.
 *
 * Deliberately NOT toISOString().slice(0, 10), which yields the UTC date and reintroduces the bug.
 */
export const localTodayISODate = (now: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
